/**
 * Cookie Manager - Simplified Version for Demo
 * Handles cross-domain cookies for both production and development
 */

export interface AuthTokens {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: Role | null;
  team: Team | null;
  hasResetPassword: boolean;
  createdAt: string;
  updatedAt: string;
  redirectUrl: string;
}
interface Role {
  id: string;
  name: string;
}

interface Team {
  id: string;
  name: string;
}

export class CookieManager {
  private static config = {
    authDomain: "http://localhost:3000",
    cookieDomain: "localhost",
    isDevelopment: process.env.NODE_ENV === "development",
    roleRedirectMapping: {
      admin:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3001"
          : "https://app.admin.theimmigrationgurus.ai",
      super_admin:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3001"
          : "https://app.admin.theimmigrationgurus.ai",
      crm_user:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3002"
          : "https://app.crm.theimmigrationgurus.ai",
      agent:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3002"
          : "https://app.crm.theimmigrationgurus.ai",
      student:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3003"
          : "https://app.students.theimmigrationgurus.ai",
      eglope_user:
        process.env.NODE_ENV === "development"
          ? "http://localhost:3004"
          : "https://app.eglope.theimmigrationgurus.ai",
    },
  };

  static configure(newConfig: Partial<typeof this.config>) {
    this.config = { ...this.config, ...newConfig };
  }

  static setAuthTokens(tokens: AuthTokens, user?: User): void {
    const { accessToken } = tokens;

    // Cookie options for production vs development
    const cookieOptions = {
      maxAge: 15 * 60, // 15 minutes
      secure: !this.config.isDevelopment, // HTTPS only in production
      sameSite: this.config.isDevelopment ? "lax" : "none", // "none" for cross-site in production
      httpOnly: false, // Can't set httpOnly from client-side JavaScript
    };

    // Access token - shorter expiry
    this.setCookie("tig_access_token", accessToken, cookieOptions);

    // User info
    if (user) {
      this.setCookie("tig_user_info", JSON.stringify(user), cookieOptions);
    }

    // Authentication flag
    this.setCookie("tig_authenticated", "true", cookieOptions);

    console.log(
      `🍪 Auth tokens set for domain: ${this.config.isDevelopment ? "localhost" : ".theimmigrationgurus.ai"}`
    );
  }

  static getAccessToken(): string | null {
    return this.getCookie("tig_access_token");
  }

  static getUserInfo(): User | null {
    const userInfoStr = this.getCookie("tig_user_info");
    if (!userInfoStr) return null;

    try {
      return JSON.parse(userInfoStr);
    } catch {
      return null;
    }
  }

  static isAuthenticated(): boolean {
    const authFlag = this.getCookie("tig_authenticated");
    const token = this.getAccessToken();
    return !!(authFlag && token);
  }

  static clearAuthTokens(): void {
    this.deleteCookie("tig_access_token");
    this.deleteCookie("tig_refresh_token");
    this.deleteCookie("tig_user_info");
    this.deleteCookie("tig_authenticated");
    console.log("🗑️ Auth cookies cleared");
  }

  static getRedirectUrlForRole(role: string): string {
    const mapping = this.config.roleRedirectMapping as Record<string, string>;
    return mapping[role] || mapping.admin || "http://localhost:3001";
  }

  static testCookies(): boolean {
    const testValue = "test_" + Date.now();
    this.setCookie("tig_test_cookie", testValue, { maxAge: 60 });
    const retrieved = this.getCookie("tig_test_cookie");
    this.deleteCookie("tig_test_cookie");
    return retrieved === testValue;
  }

  /**
   * Debug method to check current cookie state
   */
  static debugCookies(): void {
    console.log("🔍 Cookie Debug Info:");
    console.log(
      "Environment:",
      this.config.isDevelopment ? "Development" : "Production"
    );
    console.log("Current domain:", window.location.hostname);
    console.log("All cookies:", document.cookie);
    console.log("Auth cookies:");
    console.log(
      "  - tig_access_token:",
      this.getAccessToken() ? "✅ Present" : "❌ Missing"
    );
    console.log(
      "  - tig_user_info:",
      this.getUserInfo() ? "✅ Present" : "❌ Missing"
    );
    console.log(
      "  - tig_authenticated:",
      this.getCookie("tig_authenticated") ? "✅ Present" : "❌ Missing"
    );
  }

  private static setCookie(name: string, value: string, options: any): void {
    const parts = [`${name}=${encodeURIComponent(value)}`, "path=/"];

    // Set domain for cross-subdomain sharing
    if (this.config.isDevelopment) {
      // For localhost development - don't set domain to allow port-based subdomains
      // parts.push(`domain=localhost`);
    } else {
      // For production - use parent domain to share across all subdomains
      parts.push(`domain=.theimmigrationgurus.ai`);
    }

    if (options.maxAge) parts.push(`max-age=${options.maxAge}`);
    if (options.secure) parts.push("secure");
    if (options.sameSite) parts.push(`samesite=${options.sameSite}`);

    const cookieString = parts.join("; ");
    document.cookie = cookieString;

    console.log(`🍪 Setting cookie: ${cookieString}`);
  }

  private static getCookie(name: string): string | null {
    if (typeof document === "undefined") return null;

    const cookies = document.cookie.split("; ");
    const cookie = cookies.find((row) => row.startsWith(`${name}=`));

    if (!cookie) return null;

    const value = cookie.split("=").slice(1).join("="); // Handle values with = in them
    return value ? decodeURIComponent(value) : null;
  }

  private static deleteCookie(name: string): void {
    // Delete for current domain/path
    document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`;

    // Also delete with explicit domain for cross-subdomain cleanup
    if (!this.config.isDevelopment) {
      document.cookie = `${name}=; path=/; domain=.theimmigrationgurus.ai; expires=Thu, 01 Jan 1970 00:00:01 GMT`;
    }

    console.log(`🗑️ Deleting cookie: ${name}`);
  }
}
